import { AxiosError } from 'axios'

/**
 * Extracts a user-friendly error message from an API error object.
 * Handles various Django Rest Framework error formats.
 * 
 * @param error The error object (usually from Axios)
 * @param defaultMessage Fallback message if no specific error is found
 * @returns A string containing the error message(s)
 */
export const getErrorMessage = (error: unknown, defaultMessage = 'An unexpected error occurred'): string => {
    if (!error || typeof error !== 'object') {
        return defaultMessage
    }

    const err = error as AxiosError<any>

    if (err.response && err.response.data) {
        const data = err.response.data

        // Case 1: Simple "error" key
        if (data.error && typeof data.error === 'string') {
            return data.error
        }

        // Case 2: DRF "detail" key
        if (data.detail && typeof data.detail === 'string') {
            return data.detail
        }

        // Case 3: Array of errors (e.g. ["Invalid credentials"])
        if (Array.isArray(data)) {
            return data.join('\n')
        }

        // Case 4: "errors" object or direct field errors
        const errors = data.errors || data

        if (errors && typeof errors === 'object') {
            const messages: string[] = []

            for (const key in errors) {
                const value = errors[key]

                let fieldLabel = key
                // Clean up: non_field_errors -> '', integers -> '' (for arrays treated as objects)
                if (key === 'non_field_errors' || !isNaN(Number(key))) {
                    fieldLabel = ''
                } else {
                    fieldLabel = key.charAt(0).toUpperCase() + key.slice(1) + ': '
                }

                if (Array.isArray(value)) {
                    messages.push(`${fieldLabel}${value.join(', ')}`)
                } else if (typeof value === 'string') {
                    messages.push(`${fieldLabel}${value}`)
                }
            }

            if (messages.length > 0) {
                return messages.join('\n')
            }
        }
    }

    // Fallback to JS error message
    if (error instanceof Error) {
        return error.message
    }

    return defaultMessage
}
